import { Dispatch, SetStateAction, useCallback, useState } from 'react'
import { useMutation } from '@apollo/client'
import Button from '@mui/material/Button'
import { removeQuestion, removeQuestionVariables } from '__generatedTypes__/removeQuestion'
import { REMOVE_QUESTION } from 'queries/question'
import ConfirmationModal from 'components/Modals/ConfirmationModal'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { QUERY_PARAM, QUESTIONS_TAB, QUESTION_TEMPLATES_TAB } from 'pagesNew/Questions/QuestionTabs'
import { toast } from 'utils/toast'

export type RemoveButtonProps = {
  questionId: string
  submitting: boolean
  setSubmitting: (submitting: boolean) => void
  setRemoveError?: Dispatch<SetStateAction<boolean>>
  isTemplate?: boolean
}

export const RemoveButton = ({
  questionId,
  submitting,
  setSubmitting,
  setRemoveError,
  isTemplate = false,
}: RemoveButtonProps) => {
  const { navigate } = useLink()
  const [removeQuestion] = useMutation<removeQuestion, removeQuestionVariables>(REMOVE_QUESTION)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleRemove = useCallback(async () => {
    if (submitting) {
      return
    }

    setSubmitting(true)
    try {
      await removeQuestion({
        variables: {
          id: questionId,
        },
      })
      setRemoveError?.(false)
      toast.success(isTemplate ? 'Question template deleted successfully' : 'Question deleted successfully')

      navigate(
        cmsRoutes.QUESTION_LIST,
        {},
        {
          searchParams: new URLSearchParams({
            [QUERY_PARAM]: isTemplate ? QUESTION_TEMPLATES_TAB.id : QUESTIONS_TAB.id,
          }),
        }
      )
    } catch (e) {
      console.error(e)
      setRemoveError?.(true)
      toast.error(isTemplate ? 'Failed to delete question template' : 'Failed to delete question')
    }
    setIsModalOpen(false)
    setSubmitting(false)
  }, [questionId, submitting, navigate])

  return (
    <>
      <Button type="button" variant="outlined" color="red" disabled={submitting} onClick={() => setIsModalOpen(true)}>
        Delete
      </Button>
      <ConfirmationModal
        open={isModalOpen}
        title={isTemplate ? 'Deleting Question Template' : 'Deleting Question'}
        subtitle={
          <>
            Are you sure you want to delete this <br />
            {isTemplate ? 'question template' : 'question'}? This action cannot be changed.
          </>
        }
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleRemove}
      />
    </>
  )
}
